import { useImmer } from "use-immer";
import { useRef, useState, useEffect } from "react";
import SimpleReactValidator from "simple-react-validator";
import { useSearchParams } from "react-router-dom";
import { useRecipeStockItemsQueries } from "./useRecipeStockItemsQueries";

export const useRecipeStockItems = ({ recipeId, alternativeunitList = [] }) => {
  const [, forceUpdate] = useState();
  const [searchParams, setSearchParams] = useSearchParams();

  // Validator
  const validator = useRef(
    new SimpleReactValidator({
      autoForceUpdate: { forceUpdate: forceUpdate },
      element: (message) => message,
    }),
  );

  // State management with useImmer
  const [state, setState] = useImmer({
    deleteInfo: { show: false, id: null },
    openAddModal: false,
    editStockId: null, // Track which stock item is being edited
    formData: {
      stock_item_name: "",
      stock_category_id: null,
      stock_subcategory_id: null,
      stock_tag_id: null,
      is_alternate_UOM: false,
      total_weight: "",
      total_cost_exc_gst: "",
      status: 1,
      groups: [],
      alt_units: [],
      stock_code: null,
    },
    hasFormChanged: false, // Track if form has unsaved changes
  });

  const resetDeleteInfo = () => {
    setState((draft) => {
      draft.deleteInfo = { show: false, id: null };
    });
  };

  const {
    recipeStockItemsQuery,
    recipeStockItemDetailsQuery,
    mutateDeleteStockItem,
    mutateCreateStockItem,
    mutateUpdateStockItem,
  } = useRecipeStockItemsQueries({
    recipeId,
    stockId: state.editStockId,
    resetDeleteInfo,
  });

  const handleDelete = (id) => {
    setState((draft) => {
      draft.deleteInfo = { show: true, id };
    });
  };

  const confirmDelete = () => {
    mutateDeleteStockItem.mutate({ recipeId, stockId: state.deleteInfo.id });
  };

  // Helper function to update URL parameters while preserving existing ones
  const updateModalParams = (action, stockId = null) => {
    const newParams = new URLSearchParams(searchParams);

    if (action === "open") {
      newParams.set("modal", "add-stock-item");
      if (stockId) {
        newParams.set("stockId", stockId.toString());
      } else {
        newParams.delete("stockId");
      }
    } else if (action === "close") {
      newParams.delete("modal");
      newParams.delete("stockId");
    }

    setSearchParams(newParams);
  };

  // Initialize modal state from URL parameters on mount
  useEffect(() => {
    const modalParam = searchParams.get("modal");
    const stockIdParam = searchParams.get("stockId");

    if (modalParam === "add-stock-item") {
      setState((draft) => {
        draft.openAddModal = true;
        draft.editStockId = stockIdParam ? parseInt(stockIdParam, 10) : null;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Populate form data when editing
  useEffect(() => {
    if (state.editStockId && recipeStockItemDetailsQuery.data) {
      const details = recipeStockItemDetailsQuery.data;
      setState((draft) => {
        draft.formData = details;
      });
    }
  }, [state.editStockId, recipeStockItemDetailsQuery.data, setState]);

  const handleAddItem = () => {
    setState((draft) => {
      draft.openAddModal = true;
      draft.editStockId = null; // Clear edit mode
    });
    updateModalParams("open");
  };

  const handleEditItem = (stockId) => {
    console.log("Editing stock item with ID:", stockId);
    setState((draft) => {
      draft.openAddModal = true;
      draft.editStockId = stockId; // Set edit mode
    });
    updateModalParams("open", stockId);
  };

  const handleCloseModal = () => {
    setState((draft) => {
      draft.openAddModal = false;
      draft.editStockId = null; // Clear edit mode
      // Reset form data when closing
      draft.formData = {
        stock_item_name: "",
        stock_category_id: null,
        stock_subcategory_id: null,
        stock_tag_id: null,
        is_alternate_UOM: false,
        total_weight: "",
        status: 1,
        groups: [],
        alt_units: [],
      };
      if (draft.hasFormChanged) draft.hasFormChanged = false;
    });
    validator.current.hideMessages();
    updateModalParams("close");
  };

  const handleFormChange = (field, value) => {
    setState((draft) => {
      draft.formData[field] = value;
      // Clear subcategory when category changes
      if (field === "stock_category_id") {
        draft.formData.stock_subcategory_id = null;
      }

      // When enabling alternative UoM, add default entry if alt_units is empty
      if (field === "is_alternate_UOM" && value === true) {
        if (draft.formData.alt_units.length === 0) {
          // Calculate total weight from groups
          const totalWeight = draft.formData.groups.reduce((acc, group) => {
            const weight =
              group.has_boiling_weight === 1
                ? parseFloat(group.total_boiling_weight || 0)
                : parseFloat(group.total_qty || 0);
            return acc + weight;
          }, 0);

          draft.formData.alt_units.push({
            base_unit_qty: totalWeight.toFixed(3),
            base_unit_id: 4,
            alt_unit_qty: "",
            alt_unit_id: null,
          });
        }
      }

      // When disabling alternative UoM, clear alt_units array
      if (field === "is_alternate_UOM" && value === false) {
        draft.formData.alt_units = [];
      }

      if (!draft.hasFormChanged) draft.hasFormChanged = true; // Mark form as changed on any input change
    });
  };

  const handleAddGroups = (selectedGroupsData) => {
    setState((draft) => {
      // Transform and add selected groups to formData.groups
      const transformedGroups = selectedGroupsData.map((group) => ({
        group_id: group.group_id,
        name: group.name,
        total_qty: group.total_ingredient_qty || "0.0000",
        has_boiling_weight: group.has_boiling_weight,
        total_boiling_weight: group.total_qty || "0.0000",
        total_cost: group.total_cost || "0.00",
        ingredients: group.ingredients.map((ing) => ({
          name: ing.name,
          ingredient_id: ing.ingredient_id,
          base_unit_qty: ing.base_unit_qty,
          base_unit_symbol: ing.base_unit_symbol,
        })),
      }));
      draft.formData.groups = [...draft.formData.groups, ...transformedGroups];
      if (!draft.hasFormChanged) draft.hasFormChanged = true; // Mark form as changed when groups are added
    });
  };

  const handleRemoveGroup = (index) => {
    setState((draft) => {
      // Remove group at specified index
      draft.formData.groups.splice(index, 1);
      if (!draft.hasFormChanged) draft.hasFormChanged = true;
    });
  };

  const handleSaveItem = () => {
    if (validator.current.allValid()) {
      // Calculate total weight from all groups (same logic as in AddStockItemForm)
      const calculatedTotalWeight = state.formData.groups.reduce(
        (acc, group) => {
          const weight =
            group.has_boiling_weight === 1
              ? parseFloat(group.total_boiling_weight || 0)
              : parseFloat(group.total_qty || 0);
          return acc + weight;
        },
        0,
      );

      // Calculate total cost from all groups (same logic as in AddStockItemForm)
      const calculatedTotalCost = state.formData.groups.reduce((acc, group) => {
        const cost = parseFloat(group.total_cost || 0);
        return acc + cost;
      }, 0);

      // Prepare payload for API - spread formData and override specific fields
      const payload = {
        ...state.formData,
        stock_tag_id: state.formData.stock_tag_id || null, // Ensure stock_tag_id is null if not provided
        total_weight: calculatedTotalWeight.toFixed(3),
        total_cost_exc_gst: calculatedTotalCost,
        alt_units: state.formData.alt_units.map((alt) => ({
          base_unit_qty: calculatedTotalWeight.toFixed(3), // Use calculated total weight
          base_unit_id: 4, // Always grams (same as in RecipeStockItems)
          alt_unit_qty: alt.alt_unit_qty,
          alt_unit_id: alt.alt_unit_id,
        })),
      };

      // Determine if creating or updating
      if (state.editStockId) {
        // Update existing stock item
        mutateUpdateStockItem.mutate(
          { recipeId, stockId: state.editStockId, payload },
          {
            onSuccess: () => {
              handleCloseModal();
            },
          },
        );
      } else {
        // Create new stock item
        mutateCreateStockItem.mutate(
          { recipeId, payload },
          {
            onSuccess: () => {
              handleCloseModal();
            },
          },
        );
      }
    } else {
      // Show validation errors
      validator.current.showMessages();
      forceUpdate(1);
    }
  };

  return {
    recipeStockItemsQuery,
    deleteInfo: state.deleteInfo,
    handleDelete,
    confirmDelete,
    resetDeleteInfo,
    openAddModal: state.openAddModal,
    isEdit: !!state.editStockId,
    handleAddItem,
    handleEditItem,
    handleCloseModal,
    handleSaveItem,
    formData: state.formData,
    hasFormChanged: state.hasFormChanged,
    handleFormChange,
    handleAddGroups,
    handleRemoveGroup,
    validator,
    isCreating: mutateCreateStockItem.isPending,
    isUpdating: mutateUpdateStockItem.isPending,
    isLoadingDetails: recipeStockItemDetailsQuery.isLoading,
    alternativeunitList,
  };
};
